import React, { useState, useEffect } from 'react';
import {
  Box, Text, Input, Button,
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  Portal,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';

const STACK = {
  'sans-serif': 'Helvetica Neue, Arial, sans-serif',
  serif:        'Georgia, Times New Roman, serif',
  display:      'Georgia, serif',
  mono:         'Courier New, Courier, monospace',
};

// Single-weight display fonts (no bold variant in Google Fonts)
const SINGLE_WEIGHT = new Set([
  'Bebas Neue', 'Pacifico', 'Lobster', 'Righteous',
  'Fredoka', 'Comfortaa', 'Abril Fatface', 'Yeseva One',
]);

export const FONT_LIST = [
  // Sans-serif
  { name: 'Inter',             category: 'sans-serif' },
  { name: 'Roboto',            category: 'sans-serif' },
  { name: 'Open Sans',         category: 'sans-serif' },
  { name: 'Lato',              category: 'sans-serif' },
  { name: 'Nunito',            category: 'sans-serif' },
  { name: 'Poppins',           category: 'sans-serif' },
  { name: 'DM Sans',           category: 'sans-serif' },
  { name: 'Plus Jakarta Sans', category: 'sans-serif' },
  { name: 'Outfit',            category: 'sans-serif' },
  { name: 'Raleway',           category: 'sans-serif' },
  { name: 'Montserrat',        category: 'sans-serif' },
  { name: 'Work Sans',         category: 'sans-serif' },
  { name: 'Figtree',           category: 'sans-serif' },
  // Serif
  { name: 'Playfair Display',  category: 'serif' },
  { name: 'Merriweather',      category: 'serif' },
  { name: 'Lora',              category: 'serif' },
  { name: 'EB Garamond',       category: 'serif' },
  { name: 'Libre Baskerville', category: 'serif' },
  { name: 'PT Serif',          category: 'serif' },
  { name: 'Cormorant Garamond',category: 'serif' },
  // Display
  { name: 'Oswald',            category: 'display' },
  { name: 'Bebas Neue',        category: 'display' },
  { name: 'Righteous',         category: 'display' },
  { name: 'Fredoka',           category: 'display' },
  { name: 'Pacifico',          category: 'display' },
  { name: 'Comfortaa',         category: 'display' },
  { name: 'Lobster',           category: 'display' },
  { name: 'Abril Fatface',     category: 'display' },
  { name: 'Yeseva One',        category: 'display' },
  // Mono
  { name: 'JetBrains Mono',    category: 'mono' },
  { name: 'Fira Code',         category: 'mono' },
  { name: 'Source Code Pro',   category: 'mono' },
  { name: 'IBM Plex Mono',     category: 'mono' },
  { name: 'Roboto Mono',       category: 'mono' },
  { name: 'Space Mono',        category: 'mono' },
];

const CATEGORY_COLOR = {
  'sans-serif': 'blue',
  serif:        'purple',
  display:      'orange',
  mono:         'green',
};

function buildGoogleFontsUrl() {
  const families = FONT_LIST.map(f => {
    const slug = f.name.replace(/ /g, '+');
    return SINGLE_WEIGHT.has(f.name) ? slug : `${slug}:wght@400;700`;
  }).join('&family=');
  return `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
}

const FontOption = ({ font, isSelected, onSelect }) => (
  <Box
    display="flex"
    alignItems="center"
    px={3}
    py="7px"
    gap={3}
    cursor="pointer"
    bg={isSelected ? 'brand.50' : 'transparent'}
    _hover={{ bg: isSelected ? 'brand.50' : 'gray.50' }}
    onClick={() => onSelect(font.name)}
    borderRadius="md"
    mx={1}
  >
    {/* Font sample */}
    <Text
      fontSize="xl"
      fontWeight="700"
      color={isSelected ? 'brand.600' : 'gray.700'}
      fontFamily={`'${font.name}', ${STACK[font.category]}`}
      w="36px"
      flexShrink={0}
      lineHeight={1}
    >
      Aa
    </Text>

    {/* Font name + category */}
    <Box flex={1} minW={0}>
      <Text
        fontSize="sm"
        fontWeight={isSelected ? '600' : '400'}
        color={isSelected ? 'brand.700' : 'gray.800'}
        fontFamily={`'${font.name}', ${STACK[font.category]}`}
        noOfLines={1}
      >
        {font.name}
      </Text>
      <Text fontSize="10px" color="gray.400" textTransform="capitalize">
        {font.category}
      </Text>
    </Box>

    {/* Selected indicator */}
    {isSelected && (
      <Box
        as="span"
        className="material-icons-outlined"
        fontSize="16px"
        color="brand.500"
        flexShrink={0}
      >
        check
      </Box>
    )}
  </Box>
);

const FontPicker = ({ value, onChange, placeholder = 'Choose a font…' }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');

  // Load all picker fonts once on mount
  useEffect(() => {
    const id = 'font-picker-preview-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = buildGoogleFontsUrl();
    document.head.appendChild(link);
    // Don't remove on unmount — the picker may be used again
  }, []);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelect = (fontName) => {
    onChange(fontName);
    handleClose();
  };

  const filtered = FONT_LIST.filter(f =>
    f.name.toLowerCase().includes(query.toLowerCase().trim())
  );

  const selectedFont = FONT_LIST.find(f => f.name === value);

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      isLazy
      placement="bottom-start"
      strategy="fixed"
    >
      <PopoverTrigger>
        <Button
          variant="unstyled"
          w="100%"
          h="auto"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py="7px"
          border="1px solid"
          borderColor={isOpen ? 'blue.400' : 'gray.200'}
          borderRadius="md"
          bg="white"
          fontWeight="400"
          cursor="pointer"
          _hover={{ borderColor: 'gray.300' }}
          onClick={onOpen}
        >
          {value ? (
            <Text
              fontSize="sm"
              color="gray.800"
              fontFamily={`'${value}', ${STACK[selectedFont?.category] || 'sans-serif'}`}
              noOfLines={1}
              flex={1}
              textAlign="left"
            >
              {value}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.400" flex={1} textAlign="left">{placeholder}</Text>
          )}
          <Box
            as="span"
            className="material-icons-outlined"
            fontSize="18px"
            color="gray.400"
            ml={2}
            flexShrink={0}
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          >
            expand_more
          </Box>
        </Button>
      </PopoverTrigger>

      <Portal appendToParentPortal={false}>
        <PopoverContent w="300px" shadow="xl" border="1px solid" borderColor="gray.100" borderRadius="lg" _focus={{ outline: 'none' }}>
          <PopoverBody p={0}>
            {/* Search */}
            <Box p={2} borderBottom="1px solid" borderColor="gray.100">
              <Input
                size="sm"
                placeholder="Search fonts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                borderRadius="md"
              />
            </Box>

            {/* Font list */}
            <Box maxH="280px" overflowY="auto" py={1}>
              {filtered.length === 0 ? (
                <Text fontSize="sm" color="gray.400" textAlign="center" py={6}>No fonts found</Text>
              ) : (
                filtered.map(font => (
                  <FontOption
                    key={font.name}
                    font={font}
                    isSelected={font.name === value}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </Box>

            {/* Clear selection */}
            {value && (
              <Box borderTop="1px solid" borderColor="gray.100" p={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  w="100%"
                  onClick={() => handleSelect('')}
                >
                  Clear font
                </Button>
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default FontPicker;
